/**
 * Explanation of functionality for Toast ID Handler:
 *
 * There are a few different pieces of functionality that need to be covered when handling toast IDs for market orders:
 * 1. Creating the toast ID that uniquely identifies a market order toast.
 * 2. Handling incoming events so that we can relate them back to the toast they should be associated with.
 *   - INIT events :: when we initially create the order but it hasn't been confirmed yet, we still show the toast.
 *   - ORDER events :: once the order has been confirmed we need to link it back to the toast.
 *   - ACTION events :: when we receive an action record for an order, we need to link it back to the toast.
 *
 * ## Handling events details:
 *
 * INIT events ::
 * Properties: USER , USER_ORDER_ID
 * - When we create an init event, we will have a `USER_ORDER_ID` that we can use to uniquely identify the matching ORDER event when it lands.
 *
 * ORDER EVENTS: ::
 * Properties: USER, USER_ORDER_ID, ORDER_ID
 * - These events are what we need to bridge the ACTION events back to the toast, because they are the only events that contain the ORDER_ID and the USER_ORDER_ID together.
 *
 * ACTION EVENTS: ::
 * Properties: USER, ORDER_ID
 * - These events contain the ORDER_ID but not the USER_ORDER_ID. We require the matching ORDER event to be able to bridge these back to the original toast.
 *
 * High level explanation of Order ID matching:
 * 1. Create the toast ID for the order. Create a lookup key from the INIT event to look up the toast ID using the USER and USER_ORDER_ID.
 * 2. When we receive an ORDER event, keep it cached in a map using the ORDER_ID and USER as the key. Look up the associated toast ID which we created during the INIT event handling. Look for any unhandled ACTION events which have a matching key and handle these now.
 * 3. When we receive an ACTION event, try look up the matching order using the ORDER_ID and USER as the key. If we find it, we can bridge the ACTION event back to the original toast. If not, keep it cached in a map of "unhandled action events" using the ORDER_ID and USER as the key.
 */

import { MarketId, Opaque } from '@drift/common';
import { PublicKey, WrappedEvent } from '@drift-labs/sdk';
import invariant from 'tiny-invariant';
import { MarketOrderToastId } from './MarketOrderToastStateTypes';
import { dlog } from 'src/dev';

type ToastId = MarketOrderToastId; // This is the string we will use for unique Toast IDs
type ToastIdLookupKey = Opaque<'ToastIdLookupKey', string>; // This is the string we will use to lookup up a toast ID
type UserAndOrderIdKey = Opaque<'UserAndOrderIdKey', string>; // This the the string we will use to look up cached Order or OrderAction events
export type UserOrderId = Opaque<'UserOrderId', number>;
export type OrderId = Opaque<'OrderId', number>;

type ToastIdLookupProps = {
	user: PublicKey;
	marketId: MarketId;
} & (
	| {
			userOrderId: UserOrderId;
			orderId?: OrderId;
	  }
	| {
			userOrderId?: UserOrderId;
			orderId: OrderId;
	  }
); // This is a union type that allows looking up a toast ID as long as you have either the userOrderId or the orderId

export class V2MarketOrderToastIdHandler {
	private static toastIdLookupMap: Map<ToastIdLookupKey, ToastId> = new Map();
	private static userAndOrderToastIdLookupMap: Map<UserAndOrderIdKey, ToastId> =
		new Map();
	private static unhandledActionRecordLookupMap: Map<
		UserAndOrderIdKey,
		WrappedEvent<'OrderActionRecord'>[]
	> = new Map();
	private static timestampLookupMap: Map<string, Set<number>> = new Map();

	private static generateToastId() {
		return window.crypto.randomUUID() as ToastId;
	}

	private static generateUserOrderIdKeyWithoutTimestamp(props: {
		user: PublicKey;
		userOrderId: UserOrderId;
		marketId: MarketId;
	}): string {
		return `${
			props.marketId.key
		}_${props.userOrderId.toString()}_${props.user.toString()}`;
	}

	private static generateToastIdLookupKey(props: {
		user: PublicKey;
		userOrderId: UserOrderId;
		marketId: MarketId;
		timestamp: number;
	}) {
		invariant(props.userOrderId >= 0, 'userOrderId must be non-negative');
		invariant(props.marketId, 'marketId is required');
		invariant(props.user, 'user is required');
		return `${
			props.marketId.key
		}_${props.userOrderId.toString()}_${props.timestamp.toString()}_${props.user.toString()}` as ToastIdLookupKey;
	}

	private static generateUserAndOrderIdKey(props: {
		user: PublicKey;
		orderId: OrderId;
		marketId: MarketId;
	}) {
		invariant(props.orderId >= 0, 'orderId must be non-negative');
		invariant(props.marketId, 'marketId is required');
		invariant(props.user, 'user is required');
		return `${
			props.marketId.key
		}_${props.orderId.toString()}_${props.user.toString()}` as UserAndOrderIdKey;
	}

	// TODO: re review these flows
	public static handleInitEvent(props: {
		user: PublicKey;
		userOrderId: UserOrderId;
		marketId: MarketId;
		timestamp: number;
	}) {
		const userOrderIdKey = this.generateUserOrderIdKeyWithoutTimestamp(props);
		const toastIdLookupKey = this.generateToastIdLookupKey({
			...props,
			timestamp: props.timestamp,
		});

		// Check for existing toast ID to prevent overwrites
		if (this.toastIdLookupMap.has(toastIdLookupKey)) {
			console.warn(
				'Toast ID already exists for this user order ID and timestamp, toast behavior could be unexpected'
			);
			return this.toastIdLookupMap.get(toastIdLookupKey) as ToastId;
		}

		const toastId = this.generateToastId();
		this.toastIdLookupMap.set(toastIdLookupKey, toastId);

		const timestampSet = this.timestampLookupMap.get(userOrderIdKey);
		if (timestampSet) {
			timestampSet.add(props.timestamp);
		} else {
			this.timestampLookupMap.set(userOrderIdKey, new Set([props.timestamp]));
		}

		return toastId;
	}

	public static handleOrderConfirmationWithOrderId(props: {
		user: PublicKey;
		userOrderId: UserOrderId;
		orderId: OrderId;
		marketId: MarketId;
	}) {
		return this.handleOrderEvent(props);
	}

	public static handleOrderEvent(props: {
		user: PublicKey;
		userOrderId: UserOrderId;
		orderId: OrderId;
		marketId: MarketId;
	}): {
		toastId: ToastId | null;
		unhandledActionRecords: WrappedEvent<'OrderActionRecord'>[];
	} {
		const userOrderIdKey = this.generateUserOrderIdKeyWithoutTimestamp(props);
		const timestampSet = this.timestampLookupMap.get(userOrderIdKey);

		if (!timestampSet || timestampSet.size === 0) {
			dlog(
				'MarketOrderToastIdHandler',
				'handleOrderEvent',
				'no toast ID found'
			);
			return {
				toastId: null,
				unhandledActionRecords: [],
			};
		}

		// Try all timestamps until we find a matching toastId
		for (const timestamp of timestampSet) {
			const toastIdLookupKey = this.generateToastIdLookupKey({
				...props,
				timestamp,
			});
			const toastId = this.toastIdLookupMap.get(toastIdLookupKey);
			if (toastId) {
				const userAndOrderIdKey = this.generateUserAndOrderIdKey(props);

				const previouslyHandled =
					this.userAndOrderToastIdLookupMap.has(userAndOrderIdKey);

				if (!previouslyHandled) {
					this.userAndOrderToastIdLookupMap.set(userAndOrderIdKey, toastId);
				}

				const unhandledActionRecords =
					this.unhandledActionRecordLookupMap.get(userAndOrderIdKey) ?? [];

				return {
					toastId,
					unhandledActionRecords: unhandledActionRecords,
				};
			}
		}

		// No matching toastId found for any timestamp
		return {
			toastId: null,
			unhandledActionRecords: [],
		};
	}

	public static handleActionEvent(props: {
		user: PublicKey;
		orderId: OrderId;
		marketId: MarketId;
		actionRecord: WrappedEvent<'OrderActionRecord'>;
	}): ToastId | null {
		dlog('MarketOrderToastIdHandler', 'handleActionEvent', props);
		const userAndOrderIdKey = this.generateUserAndOrderIdKey(props);

		const toastId = this.userAndOrderToastIdLookupMap.get(userAndOrderIdKey);

		if (!toastId) {
			dlog(
				'MarketOrderToastIdHandler',
				'handleActionEvent',
				'no toast ID found'
			);
			// We haven't received the ORDER event yet, so we need to cache the action record to be handled later
			const matchingUnhandledActionRecords =
				this.unhandledActionRecordLookupMap.get(userAndOrderIdKey);

			if (!matchingUnhandledActionRecords) {
				this.unhandledActionRecordLookupMap.set(userAndOrderIdKey, [
					props.actionRecord,
				]);
			} else {
				matchingUnhandledActionRecords.push(props.actionRecord);
			}

			dlog(
				'MarketOrderToastIdHandler',
				'handleActionEvent',
				'no toast ID found',
				'setting unhandled action record',
				props.actionRecord
			);

			return null;
		} else {
			dlog(
				'MarketOrderToastIdHandler',
				'handleActionEvent',
				'toast ID found',
				toastId
			);
			return toastId;
		}
	}

	/**
	 * When reading order state we require BOTH userOrderId and orderId to match an order in the toast state, because orderState may hold old/expired orders which are liable to have matching userOrderIds.
	 * @param props
	 */
	public static getToastIdToMatchWithOrderStateRead(props: {
		user: PublicKey;
		orderId: OrderId;
		marketId: MarketId;
	}) {
		invariant(
			props.orderId !== undefined,
			'orderId must be defined when handling order state read'
		);

		const userAndOrderIdKey = this.generateUserAndOrderIdKey(props);
		const toastId = this.userAndOrderToastIdLookupMap.get(userAndOrderIdKey);

		if (!toastId) {
			return null;
		}

		return toastId;
	}

	/**
	 * We can use this to get a toast ID for live events which are coming through the pipe. They can be matched based on EITHER orderId or userOrderId because live events will have unique values for each of these. If you are trying to get the toastId by matching with OrderState from the user's account you need to use `getToastIdToMatchWithOrderStateRead` instead
	 * @param props
	 * @returns
	 */
	public static getToastIdForUpdateEvent(
		props: ToastIdLookupProps
	): ToastId | null {
		invariant(
			!(props.userOrderId == undefined && props.orderId == undefined),
			'Either userOrderId or orderId must be defined when getting toast ID'
		);

		if (props.userOrderId !== undefined) {
			const userOrderIdKey = this.generateUserOrderIdKeyWithoutTimestamp(
				props as Parameters<
					typeof this.generateUserOrderIdKeyWithoutTimestamp
				>[0]
			);
			const timestampSet = this.timestampLookupMap.get(userOrderIdKey);

			if (!timestampSet || timestampSet.size === 0) {
				return null;
			}

			// Try all timestamps until we find a matching toastId
			for (const timestamp of timestampSet) {
				const toastIdLookupKey = this.generateToastIdLookupKey({
					...props,
					timestamp,
				} as Parameters<typeof this.generateToastIdLookupKey>[0]);
				const toastId = this.toastIdLookupMap.get(toastIdLookupKey);

				if (toastId) {
					return toastId;
				}
			}

			return null;
		} else {
			const userAndOrderIdKey = this.generateUserAndOrderIdKey(
				props as Parameters<typeof this.generateUserAndOrderIdKey>[0]
			);
			const toastId =
				this.userAndOrderToastIdLookupMap.get(userAndOrderIdKey) ?? null;
			return toastId;
		}
	}

	public static clearStateForToast(props: ToastIdLookupProps): void {
		const toastId = this.getToastIdForUpdateEvent(props);
		dlog(
			'MarketOrderToastIdHandler',
			'clearStateForToast',
			props,
			'toastId',
			toastId
		);

		if (!toastId) {
			// Return gracefully if nothing to clean - this can happen if cleanup was already done
			// or if the toast was never properly initialized
			dlog(
				'MarketOrderToastIdHandler',
				'clearStateForToast: no toastId found, skipping cleanup',
				props
			);
			return;
		}

		if (props.userOrderId !== undefined) {
			const userOrderIdKey = this.generateUserOrderIdKeyWithoutTimestamp(
				props as Parameters<
					typeof this.generateUserOrderIdKeyWithoutTimestamp
				>[0]
			);
			const timestampSet = this.timestampLookupMap.get(userOrderIdKey);

			if (timestampSet) {
				// Find which timestamp corresponds to this toastId
				for (const timestamp of timestampSet) {
					const toastIdLookupKey = this.generateToastIdLookupKey({
						...props,
						timestamp,
					} as Parameters<typeof this.generateToastIdLookupKey>[0]);
					const foundToastId = this.toastIdLookupMap.get(toastIdLookupKey);

					if (foundToastId === toastId) {
						// Found the matching timestamp, remove it
						this.toastIdLookupMap.delete(toastIdLookupKey);
						timestampSet.delete(timestamp);

						// Clean up empty set
						if (timestampSet.size === 0) {
							this.timestampLookupMap.delete(userOrderIdKey);
						}
						break;
					}
				}
			}
		}

		if (props.orderId !== undefined) {
			const userAndOrderIdKey = this.generateUserAndOrderIdKey(
				props as Parameters<typeof this.generateUserAndOrderIdKey>[0]
			);
			this.userAndOrderToastIdLookupMap.delete(userAndOrderIdKey);
			this.unhandledActionRecordLookupMap.delete(userAndOrderIdKey);
		}
	}
}
